import * as React from 'react';
import { t } from '@reos/i18n';
import {
  enablePublicListingPage,
  fetchOwnerApproval,
  fetchPublicListingPage,
  sendOwnerApproval,
  setPublicListingOfficeSiteFlags,
  setPublicListingPageActive,
  setPublicListingReportPublished,
  type IssuedOwnerLink,
  type OwnerApproval,
  type PublicListingPage,
} from '../lib/public-listing-page-api.js';
import { DomainApiError } from '../lib/api-error.js';
import { formatDateTime } from '../lib/properties.js';

const cs = { locale: 'cs' } as const;

/**
 * Krok UI4 — přepínač „Veřejná stránka nemovitosti" v detailu zakázky.
 *
 * Jádro (slug, izolace, RLS, stav podle `cases.stage`) je hotové od SR5 —
 * tahle karta jen zavolá `/v1/crm/public-listings*` a ukáže odkaz. Zapnutí
 * a vypnutí NEJSOU dvě cesty: obojí jde přes STEJNOU stránku (`active`),
 * reaktivace zachovává slug (odkaz, který kancelář už poslala, zase ožije).
 */
export function PublicListingCard({ caseId }: { readonly caseId: string }): React.JSX.Element {
  const [page, setPage] = React.useState<PublicListingPage | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  // Krok SCHV1: stav schválení majitelem + odkaz z posledního odeslání.
  const [owner, setOwner] = React.useState<OwnerApproval | null>(null);
  const [issued, setIssued] = React.useState<IssuedOwnerLink | null>(null);
  const [ownerEmail, setOwnerEmail] = React.useState('');
  const [ownerCopied, setOwnerCopied] = React.useState(false);

  const load = React.useCallback((): void => {
    fetchPublicListingPage(caseId).then(
      (loadedPage) => {
        setPage(loadedPage);
        setLoaded(true);
      },
      () => setLoaded(true),
    );
    /*
     * Krok SCHV1: e-mail majitele PREDVYPLNI server z karty prodávajícího
     * (`ownerContact`, tentýž zdroj jako adresát reportu majiteli) — makléřka
     * ho nemá hledat v kartě klienta. Co už makléř napsal, se nepřepisuje.
     */
    fetchOwnerApproval(caseId).then(
      (state) => {
        setOwner(state);
        const prefill = state?.ownerContact?.email ?? null;
        // Predvyplnuje se jen do PRAZDNEHO pole (a jen pri nacteni karty),
        // takze adresu, kterou makler prepsal nebo smazal, nic nevrati.
        if (prefill !== null) setOwnerEmail((current) => (current === '' ? prefill : current));
      },
      () => setOwner(null),
    );
  }, [caseId]);

  React.useEffect(load, [load]);

  function errorText(cause: unknown): string {
    return cause instanceof DomainApiError ? cause.message : cause instanceof Error ? cause.message : String(cause);
  }

  /**
   * Krok UXP4b bod 5 (nalez P2-10): „stranka nema minimum" neni porucha, ale
   * VETA, co doplnit — ukaze se tak, jak ji rekl server, bez obalu
   * „Nepodařilo se…". Pravidlo zustava na SERVERU (`crm/public-listing-incomplete`),
   * administrace si ho neopisuje.
   */
  function showError(cause: unknown): void {
    // `type` chodi jako PLNA ADRESA dokumentace chyby
    // (`https://docs.reos.local/errors/crm/public-listing-incomplete`),
    // proto `includes`, ne rovnost — stejne jako jinde v administraci.
    if (cause instanceof DomainApiError && (cause.type ?? '').includes('crm/public-listing-incomplete')) {
      setError(cause.message);
      return;
    }
    setError(t('webAdmin.caseDetail.publicListing.error', { ...cs, values: { error: errorText(cause) } }));
  }

  function toggleActive(): void {
    setBusy(true);
    setError(null);
    setCopied(false);
    const action = page === null || !page.active ? enablePublicListingPage(caseId) : setPublicListingPageActive(caseId, false);
    void action
      .then((updated) => setPage(updated))
      .catch((cause: unknown) => { showError(cause); })
      .finally(() => setBusy(false));
  }

  function toggleReportPublished(checked: boolean): void {
    setBusy(true);
    setError(null);
    void setPublicListingReportPublished(caseId, checked)
      .then((updated) => setPage(updated))
      .catch((cause: unknown) => { showError(cause); })
      .finally(() => setBusy(false));
  }

  /**
   * Krok NAP10 (A23): přepínače webu kanceláře. Posílá se JEN změněný
   * příznak (druhý si server drží), aby „zveřejnit referenci" nikdy
   * nepřepnulo „zobrazit ve výpisu" na výchozí hodnotu.
   */
  function toggleOfficeSiteFlag(flag: 'showOnOfficeSite' | 'referencePublished', checked: boolean): void {
    setBusy(true);
    setError(null);
    void setPublicListingOfficeSiteFlags(caseId, { [flag]: checked })
      .then((updated) => setPage(updated))
      .catch((cause: unknown) => { showError(cause); })
      .finally(() => setBusy(false));
  }

  function copyLink(): void {
    if (page === null) return;
    void navigator.clipboard?.writeText(page.publicUrl).then(() => setCopied(true));
  }

  /**
   * Krok SCHV1 bod 1 — „Poslat majiteli ke schválení". Odkaz s tokenem
   * vzniká na SERVERU a vrací se jen tomuhle jednomu volání (v databázi je
   * sha256), proto si ho karta drží ve stavu: kopírování i WhatsApp berou
   * TENTÝŽ odkaz, který právě odešel e-mailem. Další odeslání vydá nový.
   */
  function sendToOwner(): void {
    setBusy(true);
    setError(null);
    setOwnerCopied(false);
    void sendOwnerApproval(caseId, { email: ownerEmail })
      .then((issued) => {
        setIssued(issued);
        setOwner(issued);
      })
      .catch((cause: unknown) => {
        showError(cause);
      })
      .finally(() => setBusy(false));
  }

  function copyOwnerLink(): void {
    if (issued === null) return;
    void navigator.clipboard?.writeText(issued.url).then(() => setOwnerCopied(true));
  }

  /**
   * Věta o stavu schválení. Pravidlo „smí to na portál" počítá server
   * (`approvedForExport`), administrace ho jen vypráví — proto se tady
   * nesčítá nic z `state` a `contentChanged` podruhé.
   */
  function ownerStateText(state: OwnerApproval): string {
    if (state.state === 'not_sent') return t('webAdmin.caseDetail.publicListing.owner.stateNotSent', cs);
    if (state.expired) return t('webAdmin.caseDetail.publicListing.owner.stateExpired', cs);
    if (state.state === 'pending') {
      return t('webAdmin.caseDetail.publicListing.owner.statePending', {
        ...cs,
        values: { sent: state.sentAt === null ? '' : formatDateTime(state.sentAt) },
      });
    }
    const decided = state.decidedAt === null ? '' : formatDateTime(state.decidedAt);
    return state.state === 'approved'
      ? t('webAdmin.caseDetail.publicListing.owner.stateApproved', { ...cs, values: { decided } })
      : t('webAdmin.caseDetail.publicListing.owner.stateObjections', { ...cs, values: { decided } });
  }

  if (!loaded) return <></>;

  const active = page?.active ?? false;

  return (
    <section className="rh-a2-card" data-testid="case-public-listing">
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.caseDetail.publicListing.heading', cs)}</h2>
      </div>
      <p className="rh-a2-card__hint">{t('webAdmin.caseDetail.publicListing.hint', cs)}</p>
      <p className="rh-a2-status" data-testid="case-public-listing-status">
        {active
          ? t('webAdmin.caseDetail.publicListing.statusOn', cs)
          : t('webAdmin.caseDetail.publicListing.statusOff', cs)}
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {/*
          Krok UXP4b bod 5 (nalez P2-10): u ZAPNUTE stranky je hlavni akce
          „Otevřít náhled" — makléř se chce podivat, co klient vidi. „Vypnout
          stránku" bylo do UXP4b plne (primarni) tlacitko, takze nejvic
          volala akce, kterou makléř chce nejmene.
        */}
        {active && page !== null ? (
          <>
            <a
              className="rh-a2-btn rh-a2-btn--primary"
              href={page.publicUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="case-public-listing-preview"
            >
              {t('webAdmin.caseDetail.publicListing.previewButton', cs)}
            </a>
            <button
              type="button"
              className="rh-a2-btn"
              onClick={copyLink}
              data-testid="case-public-listing-copy"
            >
              {t('webAdmin.caseDetail.publicListing.copyButton', cs)}
            </button>
          </>
        ) : null}
        <button
          type="button"
          className={active ? 'rh-a2-btn' : 'rh-a2-btn rh-a2-btn--primary'}
          disabled={busy}
          onClick={toggleActive}
          data-testid="case-public-listing-toggle"
        >
          {active
            ? t('webAdmin.caseDetail.publicListing.disableButton', cs)
            : t('webAdmin.caseDetail.publicListing.enableButton', cs)}
        </button>
      </div>

      {active && page !== null ? (
        <>
          <p className="rh-a2-meta" style={{ margin: '10px 0 0' }} data-testid="case-public-listing-link">
            {t('webAdmin.caseDetail.publicListing.linkLabel', cs)}: {page.publicUrl}
          </p>
          <label className="rh-a2-field rh-a2-field--check" style={{ marginTop: 10 }}>
            <input
              type="checkbox"
              checked={page.reportPublished}
              disabled={busy}
              onChange={(e) => toggleReportPublished(e.target.checked)}
              data-testid="case-public-listing-consent"
            />
            <span>{t('webAdmin.caseDetail.publicListing.consentLabel', cs)}</span>
          </label>
          <p className="rh-a2-meta" style={{ margin: '4px 0 0' }}>
            {t('webAdmin.caseDetail.publicListing.consentHint', cs)}
          </p>
          {/*
            Krok NAP10 (A23): web kanceláře. „Zobrazit na webu kanceláře" je
            VÝPIS webu (`/w/<slug>`), ne vlastní stránka nabídky — vypnutý
            přepínač nabídku z `/n/<slug>` neschová. „Zveřejnit jako
            referenci" se na webu projeví až u uzavřené zakázky (blok
            „prodali jsme"), souhlas ale kancelář může dát dřív.
          */}
          <label className="rh-a2-field rh-a2-field--check" style={{ marginTop: 10 }}>
            <input
              type="checkbox"
              checked={page.showOnOfficeSite}
              disabled={busy}
              onChange={(e) => toggleOfficeSiteFlag('showOnOfficeSite', e.target.checked)}
              data-testid="case-public-listing-office-site"
            />
            <span>{t('webAdmin.caseDetail.publicListing.officeSiteLabel', cs)}</span>
          </label>
          <p className="rh-a2-meta" style={{ margin: '4px 0 0' }}>
            {t('webAdmin.caseDetail.publicListing.officeSiteHint', cs)}
          </p>
          <label className="rh-a2-field rh-a2-field--check" style={{ marginTop: 10 }}>
            <input
              type="checkbox"
              checked={page.referencePublished}
              disabled={busy}
              onChange={(e) => toggleOfficeSiteFlag('referencePublished', e.target.checked)}
              data-testid="case-public-listing-reference"
            />
            <span>{t('webAdmin.caseDetail.publicListing.referenceLabel', cs)}</span>
          </label>
          <p className="rh-a2-meta" style={{ margin: '4px 0 0' }}>
            {t('webAdmin.caseDetail.publicListing.referenceHint', cs)}
          </p>

          {/*
            Krok SCHV1 (prohlídka u MV reality, bod 3): makléřka dnes odkaz
            posílá majiteli přes WhatsApp a čeká na „nemám připomínky". Tady
            je to jedna akce, která o tom nechá doklad — a upozornění, dokud
            souhlas není (export na portály je v /portaly, kam makléř jde až
            po téhle kartě).
          */}
          <h3 style={{ margin: '14px 0 0' }}>{t('webAdmin.caseDetail.publicListing.owner.heading', cs)}</h3>
          <p className="rh-a2-card__hint">{t('webAdmin.caseDetail.publicListing.owner.hint', cs)}</p>
          <p className="rh-a2-status" data-testid="case-owner-approval-status">
            {owner === null
              ? t('webAdmin.caseDetail.publicListing.owner.stateNotSent', cs)
              : ownerStateText(owner)}
          </p>
          {owner !== null && owner.state === 'objections' && owner.note !== null ? (
            <p className="rh-a2-meta" data-testid="case-owner-approval-note">
              {t('webAdmin.caseDetail.publicListing.owner.noteLabel', cs)}: {owner.note}
            </p>
          ) : null}
          {owner !== null && owner.contentChanged ? (
            <p className="rh-a2-meta" data-testid="case-owner-approval-changed">
              {t('webAdmin.caseDetail.publicListing.owner.contentChanged', cs)}
            </p>
          ) : null}
          {owner === null || !owner.approvedForExport ? (
            <p role="alert" className="rh-a2-meta" data-testid="case-owner-approval-warning">
              {t('webAdmin.caseDetail.publicListing.owner.exportWarning', cs)}
            </p>
          ) : null}
          <p className="rh-a2-meta" style={{ margin: '6px 0 0' }}>
            {t('webAdmin.caseDetail.publicListing.owner.contactHint', cs)}
          </p>
          {owner !== null && owner.state !== 'not_sent' ? (
            <p className="rh-a2-meta" data-testid="case-owner-approval-resend-hint">
              {t('webAdmin.caseDetail.publicListing.owner.resendHint', cs)}
            </p>
          ) : null}
          <label className="rh-a2-field" style={{ marginTop: 10 }}>
            <span>{t('webAdmin.caseDetail.publicListing.owner.emailLabel', cs)}</span>
            <input
              type="email"
              value={ownerEmail}
              disabled={busy}
              onChange={(e) => setOwnerEmail(e.target.value)}
              data-testid="case-owner-approval-email"
            />
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
            <button
              type="button"
              className="rh-a2-btn rh-a2-btn--primary"
              disabled={busy}
              onClick={sendToOwner}
              data-testid="case-owner-approval-send"
            >
              {owner === null || owner.state === 'not_sent'
                ? t('webAdmin.caseDetail.publicListing.owner.sendButton', cs)
                : t('webAdmin.caseDetail.publicListing.owner.resendButton', cs)}
            </button>
            {issued !== null ? (
              <>
                <button
                  type="button"
                  className="rh-a2-btn"
                  onClick={copyOwnerLink}
                  data-testid="case-owner-approval-copy"
                >
                  {t('webAdmin.caseDetail.publicListing.owner.copyButton', cs)}
                </button>
                <a
                  className="rh-a2-btn"
                  href={issued.whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="case-owner-approval-whatsapp"
                >
                  {t('webAdmin.caseDetail.publicListing.owner.whatsappButton', cs)}
                </a>
              </>
            ) : null}
          </div>
          {issued !== null ? (
            <p className="rh-a2-meta" style={{ margin: '8px 0 0' }} data-testid="case-owner-approval-link">
              {t('webAdmin.caseDetail.publicListing.owner.linkLabel', cs)}: {issued.url}
            </p>
          ) : null}
          {issued?.mailedTo !== null && issued?.mailedTo !== undefined ? (
            <p role="status" className="rh-a2-meta" data-testid="case-owner-approval-mailed">
              {t('webAdmin.caseDetail.publicListing.owner.mailed', {
                ...cs,
                values: { email: issued.mailedTo },
              })}
            </p>
          ) : null}
          {ownerCopied ? (
            <p role="status" className="rh-a2-meta" data-testid="case-owner-approval-copied">
              {t('webAdmin.caseDetail.publicListing.owner.copied', cs)}
            </p>
          ) : null}
        </>
      ) : null}

      {copied ? (
        <p role="status" className="rh-a2-meta" style={{ margin: '10px 0 0' }} data-testid="case-public-listing-copied">
          {t('webAdmin.caseDetail.publicListing.copied', cs)}
        </p>
      ) : null}
      {error !== null ? (
        <p role="alert" className="rh-a2-error" style={{ margin: '10px 0 0' }} data-testid="case-public-listing-error">
          {error}
        </p>
      ) : null}
    </section>
  );
}
